import type { WebDictionary } from "@/lib/i18n/locales";

/**
 * A create whose charge may have reached the processor ends in one of these
 * codes. The server keeps the appointment and retains the error on the
 * idempotency key, so the flow must stop: a second Pay press saves another
 * card and reuses the key with a different request. The quote page moves to
 * its durable pending state; every other booking surface stops on the
 * in-flow pending screen (`pendingPaymentNoticeFor`).
 */
const PENDING_PAYMENT_ERROR_CODES = new Set([
  "PAYMENT_OUTCOME_PENDING",
  "PAYMENT_STATUS_UNKNOWN",
  "PAYMENT_PERSISTENCE_FAILED",
  "PAYMENT_CLEANUP_FAILED",
]);

export function isPendingPaymentErrorCode(
  errorCode: string | undefined,
): boolean {
  return Boolean(errorCode && PENDING_PAYMENT_ERROR_CODES.has(errorCode));
}

export function dismissForPendingPayment(
  errorCode: string | undefined,
  onPaymentPending: (() => void) | undefined,
  onClose: () => void,
): boolean {
  if (!isPendingPaymentErrorCode(errorCode) || !onPaymentPending) {
    return false;
  }

  onPaymentPending();
  onClose();
  return true;
}

/**
 * What the pending screen tells the customer. `PAYMENT_PERSISTENCE_FAILED`
 * means the charge succeeded but the booking could not be finalized, so it
 * may say the payment went through; the others are unconfirmed.
 */
export type PendingPaymentNotice = "charged" | "unconfirmed";

export function pendingPaymentNoticeFor(
  errorCode: string | undefined,
): PendingPaymentNotice | null {
  if (!isPendingPaymentErrorCode(errorCode)) return null;
  return errorCode === "PAYMENT_PERSISTENCE_FAILED" ? "charged" : "unconfirmed";
}

/**
 * Customer copy for a reused create key, instead of the API's developer
 * message ("Idempotency-Key was reused with a different request").
 */
export function idempotencyErrorMessage(
  errorCode: string | undefined,
  t: Pick<WebDictionary, "bookingInProgress" | "bookingMayExist">,
): string | null {
  if (errorCode === "IDEMPOTENCY_KEY_PROCESSING") return t.bookingInProgress;
  if (errorCode === "IDEMPOTENCY_KEY_CONFLICT") return t.bookingMayExist;
  return null;
}
